/**
 * ActivePeriodCard - Period info, estimated payout, claim button (if claim_required)
 */

import React from 'react';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { usePublicActivePeriodSummary, useSubmitDistributionClaim } from '../../hooks/useDistribution';
import type { DistributionPeriod } from '../../api/distribution';

interface ActivePeriodCardProps {
  projectSlug: string;
  isClaimRequired?: boolean;
  hasClaimed?: boolean;
}

export const ActivePeriodCard: React.FC<ActivePeriodCardProps> = ({
  projectSlug,
  isClaimRequired = false,
  hasClaimed = false,
}) => {
  const { data, isLoading } = usePublicActivePeriodSummary(projectSlug);
  const claimMutation = useSubmitDistributionClaim(projectSlug);

  if (isLoading) return null;
  if (!data?.period) return null;

  const period = data.period;

  return (
    <div className="flex flex-col gap-3 rounded border border-solid border-green-600/30 bg-green-600/5 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="success">Active</Badge>
          <span className="text-body-bold font-body-bold text-default-font">
            Period #{period.period_number}
          </span>
        </div>
        <span className="text-caption font-caption text-subtext-color">
          Started {new Date(period.started_at).toLocaleDateString()}
        </span>
      </div>

      {period.notes && (
        <p className="text-body font-body text-default-font">{period.notes}</p>
      )}

      {isClaimRequired && !hasClaimed && (
        <div className="flex items-center gap-3">
          <Button
            variant="brand-primary"
            size="small"
            disabled={claimMutation.isPending}
            onClick={() => claimMutation.mutate()}
          >
            {claimMutation.isPending ? 'Claiming...' : 'Claim Rewards'}
          </Button>
          <span className="text-caption font-caption text-subtext-color">
            Register to receive rewards for this period
          </span>
        </div>
      )}

      {isClaimRequired && hasClaimed && (
        <span className="text-caption font-caption text-green-700">
          You have claimed for this period
        </span>
      )}

      {claimMutation.error && (
        <span className="text-caption font-caption text-red-600">{claimMutation.error.message}</span>
      )}
    </div>
  );
};
